import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "./RichTextEditor";
import { useUpdateTerm } from "@/hooks/useUpdateTerm";
import { usePublishTerm } from "@/hooks/usePublishTerm";
import { useAuthContext } from "@/components/AuthProvider";
import { Send, Save } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface EditTermDialogProps {
  open: boolean;
  onClose: () => void;
  termId: string;
  initialContent: string;
}

export const EditTermDialog = ({ open, onClose, termId, initialContent }: EditTermDialogProps) => {
  const { profile } = useAuthContext();
  const [content, setContent] = useState(initialContent);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  
  const updateTermMutation = useUpdateTerm();
  const publishTermMutation = usePublishTerm();

  const userId = profile?.user_id || '';
  const userName = profile?.user_name || profile?.email || '';

  const handleSaveDraft = async () => {
    await updateTermMutation.mutateAsync({ termId, content, userId, userName });
    onClose();
  };

  const handleSaveAndPublish = async () => {
    // Primeiro salva as alterações
    await updateTermMutation.mutateAsync({ termId, content, userId, userName });
    // Depois publica o termo
    await publishTermMutation.mutateAsync({ termId, userId, userName });
    setShowPublishConfirm(false);
    onClose();
  };

  const isLoading = updateTermMutation.isPending || publishTermMutation.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Termo Pendente</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Digite o conteúdo do termo de uso..."
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleSaveDraft} 
              disabled={isLoading}
            >
              <Save className="mr-2 h-4 w-4" />
              {updateTermMutation.isPending ? 'Salvando...' : 'Salvar Rascunho'}
            </Button>
            <Button 
              onClick={() => setShowPublishConfirm(true)} 
              disabled={isLoading}
            >
              <Send className="mr-2 h-4 w-4" />
              Salvar e Disparar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Publicação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja salvar e publicar este termo? Esta ação irá:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Salvar as alterações do termo</li>
                <li>Arquivar o termo atual</li>
                <li>Publicar este novo termo</li>
                <li>Enviar notificação para todos os usuários</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndPublish} disabled={isLoading}>
              {isLoading ? 'Processando...' : 'Confirmar e Disparar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
